import React from 'react';

import {
  get, isArray, isEmpty, isNil,
} from 'lodash';

import {
  ArrayField,
  DateField,
  RecordContextProvider,
  useRecordContext,
  SelectField,
  FunctionField,
  useRedirect,
  DatagridConfigurable,
  SelectColumnsButton,
  usePermissions,
} from 'react-admin';
import { Route, Routes } from 'react-router-dom';

import {
  Alert,
  Box,
  Card,
  CardHeader,
  Divider,
  Grid,
  IconButton,
  Slide,
  Typography,
} from '@mui/material';

import { ArrowForwardIosOutlined } from '@mui/icons-material';

import useEntityData from '../../../../../hooks/useEntityData';

import actions, { Actions } from '../../../../../constants/actions';
import { boldDataGridStyle } from '../../../../../constants/style/datagridStyles';

import ImageField from '../../../../../customFields/ImageField';
import { ViewJSONDialog } from '../../../../../customFields/ViewJSON';

import NoResults from '../../../../layout/NoResults';
import PurchaseButton from '../../../../layout/button/PurchaseButton';

import EntityData from '../EntityData';

import person from '../../../../../assets/exposure/person.png';
import { SanctionListMatches } from '../../../../../utilities/schemas/sanctionListMatches';
import ColoredScoreField from '../../../../../customFields/ColoredScoreField';
import { countryChoices } from '../../../../../constants/countries';
import languages from '../../../../../constants/languages';
import { sanctionTypesLabels } from '../../../../../constants/sanctionTypes';
import SanctionDetails from './details';
import hasAccess from '../../../../../utilities/hasAccess';
import PermissionMissing from '../../../../utils/PermissionMissing';

const RedirectToEntityButton = ({ label }: { label?: string }) => {
  const redirect = useRedirect();
  const record = useRecordContext<NonNullable<SanctionListMatches['potentialMatches'][number]>>();
  if (isEmpty(record)) return null;
  return (
    <IconButton onClick={() => redirect(`${record._id}`)} aria-label={label}>
      <ArrowForwardIosOutlined />
    </IconButton>
  );
};

const SanctionListBody = ({
  data,
  handlePurchase,
  isLoading,
}: {
  data: SanctionListMatches | undefined;
  handlePurchase: () => void;
  isLoading: boolean;
}) => {
  const { permissions } = usePermissions<Actions[]>();

  return (
    <RecordContextProvider value={data}>
      <Card variant="outlined">
        <CardHeader
          title="Sanction list"
          subheader={(
            hasAccess(permissions, actions.INDIVIDUAL_GET_SANCTION_LIST) && (
              <Box display="flex" alignItems="baseline">
                <Typography>Latest update:</Typography>
                <DateField sx={{ marginLeft: 1, fontWeight: 'bold' }} source="createdAt" emptyText="-" showTime />
              </Box>
            )
          )}
          action={(
            <Box display="flex" gap={4}>
              {hasAccess(permissions, actions.INDIVIDUAL_GET_SANCTION_LIST) && (<SelectColumnsButton color="secondary" />)}
              {hasAccess(permissions, actions.INDIVIDUAL_GET_SANCTION_LIST) && (<ViewJSONDialog title="Inspect" color="secondary" />)}
              <PurchaseButton
                loading={isLoading}
                label="Get sanction list"
                onClick={handlePurchase}
                empty={!isArray(data?.potentialMatches)}
                action={actions.INDIVIDUAL_CREATE_SANCTION_LIST}
                icon={null}
                color="secondary"
              />
            </Box>
          )}
        />
        <Divider />
        {data?.isTruncated && (
          <Grid container justifyContent="center" marginTop={2}>
            <Alert severity="warning">This screening report contains truncated results because there were too many potential matches.</Alert>
          </Grid>
        )}
        {hasAccess(permissions, actions.INDIVIDUAL_GET_SANCTION_LIST) ? (
          <ArrayField source="potentialMatches">
            <DatagridConfigurable empty={<NoResults variant="h6" />} sx={{ ...boldDataGridStyle, width: '100%' }} bulkActionButtons={false}>
              <FunctionField
                label="Name"
                sx={{ whiteSpace: 'pre' }}
                render={(record: SanctionListMatches['potentialMatches'][number]) => {
                  if (!isEmpty(record.fullName)) return record.fullName;
                  const name = `${record.firstName ?? ''} ${record.lastName ?? ''}`;
                  return isEmpty(name.trim()) ? '-' : name;
                }}
              />
              <ColoredScoreField source="matchProbability" label="Match %" />
              <DateField source="birthDate" options={{ timeZone: 'UTC' }} label="Birth date" />
              <SelectField source="address.country" choices={countryChoices(languages.EN)} label="Country" />
              <FunctionField
                label="Issuing body"
                sx={{ whiteSpace: 'pre' }}
                render={(record: SanctionListMatches['potentialMatches'][number]) => {
                  if (isEmpty(record.sanctions)) return '-';
                  const issuingBodies = record.sanctions.map((sanction) => sanction.issuingBody)
                    .filter((value) => !isNil(value));
                  if (isEmpty(issuingBodies)) return '-';
                  return issuingBodies.join('\n');
                }}
              />
              <FunctionField
                label="Regime"
                sx={{ whiteSpace: 'pre' }}
                render={(record: SanctionListMatches['potentialMatches'][number]) => {
                  if (isEmpty(record.sanctions)) return '-';
                  const regimes = record.sanctions.map((sanction) => sanction.regime)
                    .filter((value) => !isNil(value));
                  if (isEmpty(regimes)) return '-';
                  return regimes.join('\n');
                }}
              />
              <FunctionField
                label="Relations"
                sx={{ whiteSpace: 'pre' }}
                render={(record: SanctionListMatches['potentialMatches'][number]) => {
                  if (isEmpty(record.relatedEntities)) return '-';
                  const relatedEntities = record.relatedEntities
                    .map((relatedEntity) => relatedEntity.entityName)
                    .filter((text) => !isNil(text));
                  if (isEmpty(relatedEntities)) return '-';
                  return relatedEntities.join('\n');
                }}
              />
              <FunctionField
                label="Sanction type"
                sx={{ whiteSpace: 'pre' }}
                render={(record: SanctionListMatches['potentialMatches'][number]) => {
                  if (isEmpty(record.sanctions)) return '-';
                  const sanctionTypes = record.sanctions
                    .map((sanction) => get(sanctionTypesLabels, sanction.sanctionType, null))
                    .filter((value) => !isNil(value));
                  if (isEmpty(sanctionTypes)) return '-';
                  return sanctionTypes.join('\n');
                }}
              />
              <ImageField label="Image" source="profileImages[0]" emptyImage={person} />
              <RedirectToEntityButton />
            </DatagridConfigurable>
          </ArrayField>
        ) : (
          <Box margin={5} display="flex" width="100%" alignItems="center" flexDirection="column">
            <PermissionMissing />
          </Box>
        )}
      </Card>
    </RecordContextProvider>
  );
};

const SanctionList = () => {
  const {
    data,
    handlePurchase,
    isLoading,
  } = useEntityData<SanctionListMatches>({
    source: 'sanction-list',
    getAction: actions.INDIVIDUAL_GET_SANCTION_LIST,
  });

  return (
    <Routes>
      <Route
        index
        element={(
          <Slide in direction="right" appear={false}>
            <Grid container spacing={6}>
              <Grid size={12}>
                <EntityData />
              </Grid>
              <Grid size={12}>
                <SanctionListBody
                  data={data}
                  handlePurchase={handlePurchase}
                  isLoading={isLoading}
                />
              </Grid>
            </Grid>
          </Slide>
        )}
      />
      <Route path=":sanctionId" element={<SanctionDetails />} />
    </Routes>
  );
};

export default SanctionList;
